import { useState, Dispatch, SetStateAction } from "react";
import LinkifyIt from "linkify-it";

interface UseTextInputProps {
  maxCharCount?: number;
  initialText?: string;
}

interface UseTextInputReturn {
  text: string;
  setText: (text: string) => void;
  backendText: string;
  setBackendText: (text: string) => void;
  charCount: number;
  links: string[];
  tags: string[];
  mentions: string[];
  selectedTags: { id: string; value: string }[];
  setSelectedTags: Dispatch<SetStateAction<{ id: string; value: string }[]>>;
  selectedMentions: { id: string; value: string }[];
  setSelectedMentions: Dispatch<
    SetStateAction<{ id: string; value: string }[]>
  >;
  rebuildBackendText: (
    overrideText?: string,
    overrideTags?: { id: string; value: string }[],
    overrideMentions?: { id: string; value: string }[]
  ) => string;
}

export const useTextInput = ({
  maxCharCount = 2000,
  initialText = "",
}: UseTextInputProps = {}): UseTextInputReturn => {
  const [text, setText] = useState<string>(initialText);
  const [backendText, setBackendText] = useState<string>(initialText);
  const [charCount, setCharCount] = useState<number>(0);
  const [links, setLinks] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<
    { id: string; value: string }[]
  >([]);
  const [selectedMentions, setSelectedMentions] = useState<
    { id: string; value: string }[]
  >([]);

  const handleTextChange = (newText: string) => {
    setText(newText);
    setCharCount(newText.length);

    // Extract links - only allow HTTPS links
    const linkify = new LinkifyIt();
    linkify.set({ fuzzyLink: false }); // Disable fuzzy link detection
    linkify.set({ fuzzyIP: false }); // Disable fuzzy IP detection
    linkify.set({ fuzzyEmail: false }); // Disable fuzzy email detection

    // Only match URLs that start with https://
    const httpsRegex = /https:\/\/[^\s]+/g;
    const allLinksSet = new Set<string>();

    // Find all HTTPS links
    const httpsMatches = newText.match(httpsRegex) || [];
    httpsMatches.forEach((url) => {
      allLinksSet.add(url); // Keep the original URL without modification
    });

    setLinks(Array.from(allLinksSet));

    // Extract hashtags
    const hashtagRegex = /#([\p{L}\p{M}\p{N}_]+)/gu;
    const foundTags = [];
    let hashtagMatch;
    while ((hashtagMatch = hashtagRegex.exec(newText)) !== null) {
      foundTags.push(hashtagMatch[1]);
    }
    setTags(foundTags);

    // Extract mentions
    const mentionRegex = /@([\p{L}\p{M}\p{N}_]+)/gu;
    const foundMentions = [];
    let mentionMatch;
    while ((mentionMatch = mentionRegex.exec(newText)) !== null) {
      foundMentions.push(mentionMatch[1]);
    }
    setMentions(foundMentions);

    // Initialize backend text if empty
    if (!backendText || backendText.trim() === "") {
      setBackendText(newText.trim());
    }
  };

  const rebuildBackendText = (
    overrideText?: string,
    overrideTags?: { id: string; value: string }[],
    overrideMentions?: { id: string; value: string }[]
  ) => {
    const useText = overrideText !== undefined ? overrideText : text;
    const useTags = overrideTags !== undefined ? overrideTags : selectedTags;
    const useMentions =
      overrideMentions !== undefined ? overrideMentions : selectedMentions;
    let newBackendText = useText;
    const entities: {
      text: string;
      replacement: string;
      position: number;
    }[] = [];
    const collator = new Intl.Collator("en", { sensitivity: "base" });

    // Process Mentions
    const mentionRegex = /@([\p{L}\p{M}\p{N}_]+)/gu;
    let mentionMatch;
    while ((mentionMatch = mentionRegex.exec(useText)) !== null) {
      const mentionText = mentionMatch[0];
      const username = mentionMatch[1];
      const position = mentionMatch.index;
      const selectedMention = useMentions.find(
        (m) => collator.compare(m.value, username) === 0
      );
      if (selectedMention) {
        entities.push({
          text: mentionText,
          replacement: `@[${selectedMention.value}](user:${selectedMention.id})`,
          position,
        });
      }
    }

    // Process Tags
    const hashtagRegex = /#([\p{L}\p{M}\p{N}_]+)/gu;
    let hashtagMatch;
    const processedTags = new Set<string>();

    while ((hashtagMatch = hashtagRegex.exec(useText)) !== null) {
      const tagText = hashtagMatch[0];
      const tagName = hashtagMatch[1];
      const position = hashtagMatch.index;

      if (processedTags.has(tagName)) continue;

      const selectedTag = useTags.find(
        (t) => collator.compare(t.value, tagName) === 0
      );

      if (selectedTag) {
        processedTags.add(tagName);
        // Find all occurrences of this tag and replace them
        const tagRegexGlobal = new RegExp(
          `#${tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\p{L}\\p{M}\\p{N}_])`,
          "gu"
        );
        let specificTagMatch;
        while (
          (specificTagMatch = tagRegexGlobal.exec(newBackendText)) !== null
        ) {
          entities.push({
            text: specificTagMatch[0],
            replacement: `#[${selectedTag.value}](tag:${selectedTag.id})`,
            position: specificTagMatch.index,
          });
        }
      }
    }
    // Sort and replace
    entities.sort((a, b) => b.position - a.position);
    entities.forEach((entity) => {
      newBackendText =
        newBackendText.substring(0, entity.position) +
        entity.replacement +
        newBackendText.substring(entity.position + entity.text.length);
    });
    return newBackendText;
  };

  return {
    text,
    setText: handleTextChange,
    backendText,
    setBackendText,
    charCount,
    links,
    tags,
    mentions,
    selectedTags,
    setSelectedTags,
    selectedMentions,
    setSelectedMentions,
    rebuildBackendText,
  };
};
